import { Box } from '@mui/material';
import { motion, useReducedMotion } from 'framer-motion';
import { Check, Play, SlidersHorizontal, Trash2, X } from 'lucide-react';
import { useEffect, useRef, type KeyboardEvent as ReactKeyboardEvent, type MouseEvent as ReactMouseEvent, type ReactNode } from 'react';
import type { ArtistThemeVariant } from '../../../contexts/ArtistThemeContext';
import { useLockPageScroll } from '../../../hooks/useLockPageScroll';
import { nextRadioIndex, themeBands, themeVariantNames } from '../../../utils/themeSwatches';
import { AudioSpectrogram } from '../../ui/AudioSpectrogram';

export interface FullscreenMenuItem {
  label: string;
  href: string;
  opensPage?: boolean;
  onSelect: (event: ReactMouseEvent<HTMLAnchorElement>) => void;
}

interface FullscreenMenuProps {
  artist?: string;
  artistImage?: string;
  items: FullscreenMenuItem[];
  themes: ArtistThemeVariant[];
  selectedThemeId: string;
  onSelectTheme: (themeId: string) => void;
  onClose: () => void;
  closeControl: ReactNode;
  compact: boolean;
  cacheState: 'idle' | 'clearing' | 'cleared';
  onCacheHoldStart: () => void;
  onCacheHoldEnd: () => void;
  onCacheClear: () => void;
}

const EASE_OUT = [0.23, 1, 0.32, 1] as const;

const FOCUSABLE = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function focusableWithin(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((node) => !node.hasAttribute('disabled') && node.getAttribute('aria-hidden') !== 'true');
}

const eyebrowSx = {
  m: 0,
  fontFamily: 'Poppins, sans-serif',
  fontSize: '12px',
  fontWeight: 700,
  letterSpacing: '0.16em',
  textTransform: 'uppercase' as const,
  color: 'var(--logoColor)',
  opacity: 0.72,
};

export function FullscreenMenu({
  artist,
  artistImage,
  items,
  themes,
  selectedThemeId,
  onSelectTheme,
  onClose,
  closeControl,
  compact,
  cacheState,
  onCacheHoldStart,
  onCacheHoldEnd,
  onCacheClear,
}: FullscreenMenuProps) {
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const reduced = useReducedMotion();
  const names = themeVariantNames(themes);
  const selectedIndex = Math.max(0, themes.findIndex((theme) => theme.id === selectedThemeId));
  useLockPageScroll(true);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const first = dialog.querySelector<HTMLElement>('.FullscreenMenuItem');
    first?.focus({ preventScroll: true });
  }, []);

  const handleKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      onClose();
      return;
    }
    if (event.key !== 'Tab' || !dialogRef.current) return;
    const nodes = focusableWithin(dialogRef.current);
    if (nodes.length === 0) return;
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    const active = document.activeElement as HTMLElement | null;
    if (event.shiftKey && (active === first || !dialogRef.current.contains(active))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (active === last || !dialogRef.current.contains(active))) {
      event.preventDefault();
      first.focus();
    }
  };

  const handleThemeKey = (event: ReactKeyboardEvent<HTMLButtonElement>, index: number) => {
    const next = nextRadioIndex(index, event.key, themes.length);
    if (next === null) return;
    event.preventDefault();
    onSelectTheme(themes[next].id);
    const group = event.currentTarget.parentElement;
    group?.querySelectorAll<HTMLButtonElement>('[role="radio"]')[next]?.focus();
  };

  const rise = (delay: number) =>
    reduced
      ? { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 }, transition: { duration: 0.2 } }
      : { initial: { opacity: 0, y: 18 }, animate: { opacity: 1, y: 0 }, exit: { opacity: 0, y: -8 }, transition: { duration: 0.42, delay, ease: EASE_OUT } };

  return (
    <motion.div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label={artist ? `${artist} menu` : 'Site menu'}
      className="FullscreenMenu"
      data-lenis-prevent
      onKeyDown={handleKeyDown}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: reduced ? 0.15 : 0.3, ease: EASE_OUT }}
      style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100dvh', zIndex: 9998, overflow: 'hidden' }}
    >
      <Box
        aria-hidden="true"
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundColor: 'var(--color5)',
          backgroundImage:
            'radial-gradient(70% 60% at 88% 8%, color-mix(in srgb, var(--logoColor2) 16%, transparent), transparent 70%), radial-gradient(60% 50% at 0% 100%, color-mix(in srgb, var(--logoColor) 9%, transparent), transparent 70%)',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: { xs: 'calc(16px + env(safe-area-inset-top, 0px))', md: '24px' },
          right: { xs: '16px', md: '40px' },
          zIndex: 2,
        }}
      >
        {closeControl}
      </Box>

      <Box
        className="FullscreenMenuContent"
        sx={{
          position: 'relative',
          zIndex: 1,
          height: '100%',
          overflowY: 'auto',
          overscrollBehavior: 'contain',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
          display: 'grid',
          gridTemplateColumns: { xs: 'auto minmax(0, 1fr)', md: 'minmax(0, 1.35fr) minmax(280px, 1fr)' },
          alignItems: { xs: 'center', md: 'center' },
          alignContent: { xs: 'start', md: 'center' },
          columnGap: { xs: '16px', md: 'clamp(32px, 6vw, 120px)' },
          rowGap: { xs: '24px', md: '28px' },
          px: { xs: '20px', md: 'clamp(40px, 7vw, 128px)' },
          pt: { xs: 'calc(84px + env(safe-area-inset-top, 0px))', md: '96px' },
          pb: { xs: 'calc(120px + env(safe-area-inset-bottom, 0px))', md: '112px' },
          boxSizing: 'border-box',
        }}
      >
        <Box component="nav" aria-label="Menu" sx={{ order: 2, gridColumn: { xs: '1 / -1', md: '1' }, minWidth: 0 }}>
          <Box
            component="ol"
            sx={{
              listStyle: 'none',
              m: 0,
              p: 0,
              display: 'grid',
              gap: { xs: '2px', md: 'clamp(2px, 0.6vh, 8px)' },
              '@media (hover: hover)': {
                '&:hover .FullscreenMenuItem:not(:hover)': { opacity: 0.42 },
              },
            }}
          >
            {items.map((item, index) => (
              <motion.li key={item.label} {...rise(0.08 + index * 0.045)}>
                <Box
                  component="a"
                  href={item.href}
                  className="FullscreenMenuItem"
                  onClick={item.onSelect}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'baseline',
                    gap: { xs: '14px', md: '18px' },
                    minHeight: '44px',
                    py: { xs: '4px', md: 'clamp(2px, 0.5vh, 6px)' },
                    pr: '12px',
                    color: 'var(--logoColor)',
                    textDecoration: 'none',
                    borderRadius: '10px',
                    transition: `opacity 0.25s cubic-bezier(0.23, 1, 0.32, 1), transform 0.35s cubic-bezier(0.23, 1, 0.32, 1)`,
                    '& .menu-label': {
                      fontFamily: 'Hanson, sans-serif',
                      fontWeight: 700,
                      fontSize: { xs: 'clamp(30px, 8.6vw, 40px)', md: 'clamp(34px, 6.4vh, 68px)' },
                      lineHeight: 1,
                      letterSpacing: '0.01em',
                    },
                    '& .menu-index': {
                      fontFamily: 'Poppins, sans-serif',
                      fontSize: { xs: '13px', md: '12px' },
                      fontWeight: 600,
                      letterSpacing: '0.12em',
                      minWidth: '22px',
                      opacity: 0.55,
                      fontVariantNumeric: 'tabular-nums',
                    },
                    '& .menu-arrow': { opacity: 0, transform: 'translateX(-6px)', transition: 'opacity 0.25s ease, transform 0.35s cubic-bezier(0.23, 1, 0.32, 1)' },
                    '&:hover, &:focus-visible': {
                      transform: 'translateX(6px)',
                      '& .menu-label': { color: 'var(--logoColor2)' },
                      '& .menu-arrow': { opacity: 1, transform: 'translateX(0)' },
                    },
                    '&:focus-visible': { outline: '2px solid var(--logoColor2)', outlineOffset: '4px' },
                    '@media (prefers-reduced-motion: reduce)': { transition: 'none', '&:hover, &:focus-visible': { transform: 'none' } },
                  }}
                >
                  <span className="menu-index" aria-hidden="true">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <span className="menu-label">{item.label}</span>
                  {item.opensPage ? (
                    <Box component="span" className="menu-arrow" aria-hidden="true" sx={{ fontFamily: 'Poppins, sans-serif', fontSize: '18px', color: 'var(--logoColor2)' }}>
                      ↗
                    </Box>
                  ) : null}
                </Box>
              </motion.li>
            ))}
          </Box>
        </Box>

        <Box
          sx={{
            order: 2,
            display: { xs: 'contents', md: 'flex' },
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: '28px',
            minWidth: 0,
            '& > .menu-avatar, & > .menu-name': { order: 1 },
            '& > .menu-colours': { order: 3 },
            '@media (max-width: 899.95px)': { '& > .menu-colours': { gridColumn: '1 / -1' } },
          }}
        >
          {artistImage ? (
            <motion.div
              className="FullscreenMenuAvatar menu-avatar"
              layoutId="artist-avatar"
              initial={reduced ? { opacity: 0 } : { opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, delay: 0.05, ease: EASE_OUT, layout: { duration: 0.4 } }}
            >
              <Box
                sx={{
                  position: 'relative',
                  width: { xs: '84px', md: 'clamp(150px, 22vh, 220px)' },
                  height: { xs: '84px', md: 'clamp(150px, 22vh, 220px)' },
                  display: 'grid',
                  placeItems: 'center',
                  flex: '0 0 auto',
                }}
              >
                <AudioSpectrogram size={compact ? '112px' : 'clamp(190px, 28vh, 280px)'} strokeWidth={compact ? '3px' : '4px'} className="audio-spectrogram" />
                <Box
                  component="img"
                  src={artistImage}
                  alt=""
                  sx={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover', boxShadow: '0 18px 40px rgba(0,0,0,0.35)' }}
                />
              </Box>
            </motion.div>
          ) : null}

          {artist ? (
            <motion.p
              {...rise(0.14)}
              className="menu-name"
              style={{ margin: 0, minWidth: 0, fontFamily: 'Hanson, sans-serif', fontWeight: 700, fontSize: '22px', lineHeight: 1.1, color: 'var(--logoColor2)' }}
            >
              {artist}
            </motion.p>
          ) : null}
          <motion.div {...rise(0.22)} className="menu-colours" style={{ minWidth: 0, width: '100%' }}>
            {themes.length > 1 ? (
              <Box>
                <Box component="p" id="menu-colour-label" sx={eyebrowSx}>
                  Colour
                </Box>
                <Box role="radiogroup" aria-labelledby="menu-colour-label" sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: '10px', md: '12px' }, mt: '10px' }}>
                  {themes.map((theme, index) => {
                    const selected = index === selectedIndex;
                    const bands = themeBands(theme);
                    return (
                      <Box
                        key={theme.id}
                        component="button"
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        aria-label={`${names[index]} colours`}
                        tabIndex={selected ? 0 : -1}
                        onClick={() => onSelectTheme(theme.id)}
                        onKeyDown={(event: ReactKeyboardEvent<HTMLButtonElement>) => handleThemeKey(event, index)}
                        sx={{
                          appearance: 'none',
                          display: 'grid',
                          justifyItems: 'start',
                          gap: '7px',
                          minWidth: '76px',
                          minHeight: '44px',
                          p: '6px 6px 8px',
                          border: '1px solid',
                          borderColor: selected ? 'color-mix(in srgb, var(--logoColor2) 70%, transparent)' : 'color-mix(in srgb, var(--logoColor) 18%, transparent)',
                          borderRadius: '14px',
                          background: selected ? 'color-mix(in srgb, var(--logoColor2) 10%, transparent)' : 'color-mix(in srgb, var(--backgroundColor) 30%, transparent)',
                          color: 'var(--logoColor)',
                          cursor: 'pointer',
                          transition: 'border-color 0.2s ease, background-color 0.2s ease, transform 0.16s ease',
                          '&:hover': { borderColor: 'color-mix(in srgb, var(--logoColor2) 55%, transparent)' },
                          '&:active': { transform: 'scale(0.97)' },
                          '&:focus-visible': { outline: '2px solid var(--logoColor2)', outlineOffset: '3px' },
                          '@media (prefers-reduced-motion: reduce)': { transition: 'none', '&:active': { transform: 'none' } },
                        }}
                      >
                        <Box
                          component="span"
                          aria-hidden="true"
                          sx={{
                            position: 'relative',
                            display: 'grid',
                            gridTemplateColumns: '1.4fr 1fr 1fr',
                            width: '64px',
                            height: '34px',
                            borderRadius: '9px',
                            overflow: 'hidden',
                            boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.12)',
                          }}
                        >
                          <span style={{ background: bands.background }} />
                          <span style={{ background: bands.surface }} />
                          <span style={{ background: `linear-gradient(180deg, ${bands.accent} 0 55%, ${bands.ink} 55% 100%)` }} />
                          {selected ? (
                            <Box
                              component="span"
                              sx={{
                                position: 'absolute',
                                left: '6px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                width: '18px',
                                height: '18px',
                                borderRadius: '50%',
                                display: 'grid',
                                placeItems: 'center',
                                backgroundColor: bands.accent,
                                color: bands.background,
                              }}
                            >
                              <Check size={12} strokeWidth={3} />
                            </Box>
                          ) : null}
                        </Box>
                        <Box component="span" sx={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px', fontWeight: selected ? 700 : 500, lineHeight: 1.1, opacity: selected ? 1 : 0.78 }}>
                          {names[index]}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            ) : null}
          </motion.div>
        </Box>
      </Box>

      <motion.div
        {...rise(0.3)}
        style={{ position: 'absolute', left: 0, right: 0, bottom: 0, zIndex: 2, pointerEvents: 'none' }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '16px',
            px: { xs: '20px', md: 'clamp(40px, 7vw, 128px)' },
            pb: { xs: 'calc(88px + env(safe-area-inset-bottom, 0px))', md: '28px' },
          }}
        >
          <Box component="ul" aria-label="Keyboard shortcuts" sx={{ display: { xs: 'none', md: 'flex' }, gap: '20px', listStyle: 'none', m: 0, p: 0 }}>
            {[
              { key: 'Space', label: 'Play or pause', Icon: Play },
              { key: 'K', label: 'Commands', Icon: SlidersHorizontal },
              { key: 'Esc', label: 'Close', Icon: X },
            ].map((hint) => (
              <Box component="li" key={hint.key} sx={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--logoColor)', fontFamily: 'Poppins, sans-serif', fontSize: '12px', opacity: 0.72 }}>
                <Box
                  component="kbd"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '5px',
                    minHeight: '24px',
                    px: '8px',
                    borderRadius: '6px',
                    border: '1px solid color-mix(in srgb, var(--logoColor) 24%, transparent)',
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: '11px',
                    fontWeight: 700,
                    color: 'var(--logoColor2)',
                    background: 'transparent',
                    boxShadow: 'none',
                  }}
                >
                  <hint.Icon size={11} strokeWidth={2.4} aria-hidden="true" />
                  {hint.key}
                </Box>
                {hint.label}
              </Box>
            ))}
          </Box>
          <Box
            component="button"
            type="button"
            aria-label="Hold to clear local cache"
            aria-pressed={cacheState === 'cleared'}
            onPointerDown={onCacheHoldStart}
            onPointerUp={onCacheHoldEnd}
            onPointerLeave={onCacheHoldEnd}
            onPointerCancel={onCacheHoldEnd}
            onKeyDown={(event: ReactKeyboardEvent<HTMLButtonElement>) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onCacheClear();
              }
            }}
            disabled={cacheState === 'clearing'}
            sx={{
              pointerEvents: 'auto',
              ml: 'auto',
              width: '44px',
              height: '44px',
              p: 0,
              appearance: 'none',
              border: 0,
              borderRadius: '999px',
              background: 'transparent',
              color: 'var(--logoColor)',
              cursor: cacheState === 'clearing' ? 'wait' : 'pointer',
              opacity: cacheState === 'cleared' ? 0.72 : 0.32,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'opacity 0.2s ease, transform 0.16s ease',
              '&:hover, &:focus-visible': { opacity: 0.85 },
              '&:focus-visible': { outline: '2px solid var(--logoColor2)', outlineOffset: '2px' },
              '&:active': { transform: 'scale(0.94)' },
            }}
          >
            <Trash2 size={14} aria-hidden="true" strokeWidth={2.2} />
          </Box>
        </Box>
      </motion.div>
    </motion.div>
  );
}

export default FullscreenMenu;
